import { injectStampAreasIntoFabricJson } from '@/image-designer/adapters/stampAreasAdapter';
import {
  buildSlideFromImageMeta,
  attachThumbToSlides,
  ensureDesignerBackgroundLayer,
  resolveSlideProductThumbUrl,
} from './slideImageUtils';
import { createWorkspaceObject, getPageSizeForRatio, DEFAULT_PAGE_RATIO_ID } from './pageSizePresets';
import {
  createDefaultMainSlideFields,
  enrichSlideHierarchyFields,
  mergeImagesMetaIntoSlides,
  reindexTemplateSlideNames,
  slidesToTemplateImagesMeta,
} from './templateImageHierarchy';

export const DEFAULT_WORKSPACE_SIZE = getPageSizeForRatio(DEFAULT_PAGE_RATIO_ID);

export function buildDesignerSlideName(index) {
  return `主图${index}`;
}

export function reindexDesignerSlideNames(slides) {
  const renamed = reindexTemplateSlideNames(slides);
  return renamed.map((slide) => {
    const thumbUrl = resolveSlideProductThumbUrl(slide);
    const enriched = { ...slide, thumbUrl: thumbUrl || slide.thumbUrl };
    return {
      ...enriched,
      fabricCanvas: ensureDesignerBackgroundLayer(enriched.fabricCanvas, enriched),
    };
  });
}

export function nextDesignerSlideName(slides) {
  return buildDesignerSlideName(slides.length + 1);
}

export function createSlideId() {
  return `slide-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

export function createEmptyFabricCanvas(
  width = DEFAULT_WORKSPACE_SIZE.width,
  height = DEFAULT_WORKSPACE_SIZE.height,
) {
  return {
    version: '5.3.0',
    objects: [createWorkspaceObject(width, height)],
  };
}

export function getWorkspaceSizeFromFabricJson(fabricJson) {
  const workspace = fabricJson?.objects?.find((o) => o.id === 'workspace');
  if (workspace?.width && workspace?.height) {
    return { width: workspace.width, height: workspace.height };
  }
  return { ...DEFAULT_WORKSPACE_SIZE };
}

export function cloneFabricJson(json) {
  return JSON.parse(JSON.stringify(json || createEmptyFabricCanvas()));
}

/**
 * 从后端模板解析为设计器幻灯片列表
 */
export function normalizeSlidesFromParsed(parsed) {
  if (!parsed) {
    const id = createSlideId();
    return {
      slides: reindexDesignerSlideNames([
        { id, name: buildDesignerSlideName(1), fabricCanvas: createEmptyFabricCanvas() },
      ]),
      activeSlideId: id,
      primaryImageId: id,
    };
  }

  const imagesMeta = (parsed.images || []).filter((img) => !img.isExample);
  const slideCanvases = parsed.slideCanvases || {};
  const slideCanvasIds = Array.isArray(parsed.slideCanvasIds)
    ? parsed.slideCanvasIds.filter(Boolean)
    : [];
  const primaryImageId = parsed.primaryImageId || parsed.id || 'slide-1';

  let slides = [];

  if (Object.keys(slideCanvases).length > 0 || slideCanvasIds.length > 0) {
    const orderedIds = slideCanvasIds.length > 0
      ? slideCanvasIds
      : Object.keys(slideCanvases);
    slides = orderedIds.map((id, idx) => {
      const fabricCanvas = slideCanvases[id];
      const pending = !fabricCanvas || fabricCanvas._externalPending;
      return {
        id,
        name: buildDesignerSlideName(idx + 1),
        fabricCanvas: pending
          ? { version: '5.3.0', objects: [], _externalPending: true }
          : cloneFabricJson(fabricCanvas),
        width: imagesMeta.find((img) => img.id === id)?.width,
        height: imagesMeta.find((img) => img.id === id)?.height,
        ...(pending ? { _needsCanvasFetch: true } : {}),
      };
    });
  } else if (parsed.fabricCanvas) {
    const id = primaryImageId;
    slides = [{
      id,
      name: buildDesignerSlideName(1),
      fabricCanvas: cloneFabricJson(parsed.fabricCanvas),
    }];
  } else if (parsed.stampAreas && Object.keys(parsed.stampAreas).length > 0) {
    const ws = {
      width: parsed.workspaceWidth || DEFAULT_WORKSPACE_SIZE.width,
      height: parsed.workspaceHeight || DEFAULT_WORKSPACE_SIZE.height,
    };
    slides = Object.keys(parsed.stampAreas).map((imageId, idx) => {
      const base = createEmptyFabricCanvas(ws.width, ws.height);
      const fabricCanvas = injectStampAreasIntoFabricJson(
        base,
        imageId,
        parsed.stampAreas,
      );
      return {
        id: imageId,
        name: buildDesignerSlideName(idx + 1),
        fabricCanvas,
      };
    });
  } else {
    const id = primaryImageId;
    slides = [{
      id,
      name: buildDesignerSlideName(1),
      fabricCanvas: createEmptyFabricCanvas(),
    }];
  }

  if (imagesMeta.length > 0) {
    const order = imagesMeta.map((img) => img.id);
    slides.sort((a, b) => {
      const ai = order.indexOf(a.id);
      const bi = order.indexOf(b.id);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });

    imagesMeta.forEach((meta) => {
      if (meta.isExample) return;
      if (!slides.some((s) => s.id === meta.id)) {
        const src = meta.thumbUrl || meta.src || meta.imageUrl;
        if (src) {
          slides.push(buildSlideFromImageMeta({ ...meta, src }));
        } else {
          slides.push({
            id: meta.id,
            name: buildDesignerSlideName(slides.length + 1),
            fabricCanvas: createEmptyFabricCanvas(),
          });
        }
      }
    });
  }

  slides = attachThumbToSlides(slides, imagesMeta);
  slides = mergeImagesMetaIntoSlides(slides, imagesMeta);
  slides = slides.map((slide) => enrichSlideHierarchyFields(slide));
  slides = reindexDesignerSlideNames(slides);

  const activeSlideId = slides.some((s) => s.id === primaryImageId)
    ? primaryImageId
    : slides[0]?.id;

  return {
    slides,
    activeSlideId,
    primaryImageId: activeSlideId,
  };
}

export function buildSlideCanvasesMap(slides, options = {}) {
  const onlyIds = options?.onlyIds;
  const allow = onlyIds ? new Set([...onlyIds].map(String)) : null;
  const map = {};
  slides.forEach((slide) => {
    if (!slide?.id) return;
    if (allow && !allow.has(String(slide.id))) return;
    if (!slide.fabricCanvas || slide.fabricCanvas._externalPending) return;
    map[slide.id] = cloneFabricJson(slide.fabricCanvas);
  });
  return map;
}

export function slidesToImagesMeta(slides) {
  return slidesToTemplateImagesMeta(slides);
}

export { createDefaultMainSlideFields, createDefaultDetailSlideFields } from './templateImageHierarchy';
